import { BUSINESS_CRITICAL_CACHE } from '@module/mdrt-org/shared/utils/constants/business-critical-cache';
import locales from '@module/mdrt-org/shared/utils/data/graphql/_generated/locales.json';
import { getBusinessCriticalPathnames } from '@module/mdrt-org/shared/utils/helpers/get-business-critical-pathnames';

import { isSameBuildAsLastPass, rememberWarmedBuild } from './build-fingerprint';
import { pickAssetUrls, pickImageUrls } from './pick-subresource-urls';
import { getPlaceholderAssetUrls, getPlaceholderImageUrls } from './placeholder-urls';
import { prune, runPool, warmPage, warmSubresource } from './warm-cache';

const MAX_IMAGES = 250;

const MAX_STATIC_ASSETS = 300;

const canContinue = (signal: AbortSignal) => !signal.aborted && navigator.onLine;

const addUpTo = (target: Set<string>, urls: string[], limit: number) => {
  for (const url of urls) {
    if (target.size >= limit) {
      return;
    }

    target.add(url);
  }
};

const warmPathnames = async ({
  cache,
  canReuse,
  headers,
  onHtml,
  pathnames,
  signal,
}: {
  cache: Cache;
  canReuse: boolean;
  headers: HeadersInit;
  onHtml?: (html: string, pathname: string) => void;
  pathnames: string[];
  signal: AbortSignal;
}) => {
  await runPool(pathnames, async (pathname) => {
    if (!canContinue(signal)) {
      return;
    }

    const response = await warmPage({ cache, canReuse, headers, pathname, signal });

    if (onHtml && response) {
      onHtml(await response.text(), pathname);
    }
  });
};

const warmSubresources = async ({
  cache,
  isImage,
  signal,
  urls,
}: {
  cache: Cache;
  isImage: boolean;
  signal: AbortSignal;
  urls: string[];
}) =>
  runPool(urls, async (url) => {
    if (signal.aborted) {
      return;
    }

    await warmSubresource({ cache, isImage, signal, url });
  });

// Warms documents, then their static chunks, then the RSC payloads, then the images, so
// that a cold offline launch has a complete page to render rather than a partial one.
// The pass stops as soon as the device drops, and only a pass that ran to the end claims
// the current build — a cut-short pass is re-run on the next launch.
//
// Only the active locale is warmed, but pruning runs against every locale: the service
// worker treats the whole localized set as business critical, so narrowing the prune to
// the active locale would delete another locale's pages out from under it — including the
// unprefixed start URL once the user switches away from the default locale.
export const warmOfflineCache = async (locale: string, signal: AbortSignal) => {
  if (!navigator.onLine) {
    return;
  }

  const pathnames = getBusinessCriticalPathnames([locale]);

  const canReuse = isSameBuildAsLastPass();

  const [htmlCache, rscCache, imageCache, assetCache] = await Promise.all([
    caches.open(BUSINESS_CRITICAL_CACHE.html),
    caches.open(BUSINESS_CRITICAL_CACHE.rsc),
    caches.open(BUSINESS_CRITICAL_CACHE.image),
    caches.open(BUSINESS_CRITICAL_CACHE.asset),
  ]);

  const imageUrls = new Set(getPlaceholderImageUrls());
  const assetUrls = new Set(getPlaceholderAssetUrls());

  await warmPathnames({
    cache: htmlCache,
    canReuse,
    headers: { Accept: 'text/html' },
    onHtml: (html, pathname) => {
      addUpTo(imageUrls, pickImageUrls(html, pathname), MAX_IMAGES);
      addUpTo(assetUrls, pickAssetUrls(html), MAX_STATIC_ASSETS);
    },
    pathnames,
    signal,
  });

  if (!canContinue(signal)) {
    return;
  }

  await warmSubresources({ cache: assetCache, isImage: false, signal, urls: [...assetUrls] });

  if (!canContinue(signal)) {
    return;
  }

  await warmPathnames({ cache: rscCache, canReuse, headers: { RSC: '1' }, pathnames, signal });

  if (!canContinue(signal)) {
    return;
  }

  await warmSubresources({ cache: imageCache, isImage: true, signal, urls: [...imageUrls] });

  const allLocalePathnames = getBusinessCriticalPathnames(locales);

  await Promise.all([prune(htmlCache, allLocalePathnames), prune(rscCache, allLocalePathnames)]);

  rememberWarmedBuild();
};
